/**
 * QuoteRecentActivity Component
 *
 * Displays recent activity for a specific quote
 */

import { Avatar, List, Skeleton, Tag, Typography, Alert } from 'antd';
import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import { MdCheckCircle, MdEdit, MdPerson, MdSwapHoriz } from 'react-icons/md';
import { IDataverseQuote, IActivityLog } from '../../types';
import { useQuoteActivityLogs } from '../../hooks';

const { Title, Text } = Typography;

export interface QuoteActivity {
  id: string;
  type: 'created' | 'status_changed' | 'note_added' | 'converted' | 'sent' | 'updated';
  user: string;
  description: string;
  timestamp: string;
  metadata?: {
    oldStatus?: string;
    newStatus?: string;
    note?: string;
  };
}

interface QuoteRecentActivityProps {
  quote?: IDataverseQuote;
  isLoading?: boolean;
}

const getActivityIcon = (type: QuoteActivity['type']) => {
  switch (type) {
    case 'created':
      return <MdCheckCircle size={20} />;
    case 'status_changed':
      return <MdSwapHoriz size={20} />;
    case 'note_added':
      return <MdEdit size={20} />;
    case 'converted':
      return <MdCheckCircle size={20} />;
    case 'sent':
      return <MdCheckCircle size={20} />;
    default:
      return <MdPerson size={20} />;
  }
};

const getActivityColor = (type: QuoteActivity['type']) => {
  switch (type) {
    case 'created':
      return 'bg-green-100 text-green-600';
    case 'status_changed':
      return 'bg-blue-100 text-blue-600';
    case 'note_added':
      return 'bg-purple-100 text-purple-600';
    case 'converted':
      return 'bg-green-100 text-green-600';
    case 'sent':
      return 'bg-orange-100 text-orange-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

export const QuoteRecentActivity: React.FC<QuoteRecentActivityProps> = ({
  quote,
  isLoading: quoteLoading = false,
}) => {
  // Fetch activity logs from API
  const { data: activityLogsResponse, isLoading: activityLoading, error } = useQuoteActivityLogs(quote?.quoteid);

  // Transform API activity logs to QuoteActivity format
  const activities: QuoteActivity[] = activityLogsResponse?.value?.map((log: IActivityLog, index: number) => {
    // Determine activity type based on subject
    let type: QuoteActivity['type'] = 'updated';
    const subject = log.subject?.toLowerCase() || '';

    if (subject.includes('created')) {
      type = 'created';
    } else if (subject.includes('converted')) {
      type = 'converted';
    } else if (subject.includes('status')) {
      type = 'status_changed';
    } else if (subject.includes('note')) {
      type = 'note_added';
    } else if (subject.includes('sent')) {
      type = 'sent';
    }

    return {
      id: log.activityid || `activity-${index}`,
      type,
      user: log['_regardingobjectid_value@OData.Community.Display.V1.FormattedValue'] || 'System',
      description: log.subject || 'Activity logged',
      timestamp: log.createdon || new Date().toISOString(),
      metadata: {
        note: log.description || undefined,
      },
    };
  }) ?? [];

  const isLoading = quoteLoading || activityLoading;

  return (
    <div className="rounded-lg bg-white">
      <div className="flex justify-start items-center px-4 py-3">
        <div>
          <Title level={5} className="!mb-1 text-left">
            Recent Activity
          </Title>
          <Text type="secondary" className="text-sm">
            Latest actions and updates for this quote
          </Text>
        </div>
      </div>

      {error && (
        <div className="px-4 pb-4">
          <Alert
            message="Error Loading Activity"
            description="Failed to load activity logs. Please try again."
            type="error"
            showIcon
          />
        </div>
      )}

      {isLoading ? (
        <List
          dataSource={[1, 2, 3, 4, 5]}
          renderItem={() => (
            <List.Item className='px-4'>
              <Skeleton avatar paragraph={{ rows: 1 }} active />
            </List.Item>
          )}
        />
      ) : activities && activities.length > 0 ? (
        <List
          dataSource={activities}
          renderItem={(activity) => (
            <List.Item className="hover:bg-gray-50 px-4 transition-colors">
              <List.Item.Meta
                avatar={
                  <Avatar className={getActivityColor(activity.type)}>
                    {getActivityIcon(activity.type)}
                  </Avatar>
                }
                title={
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Text strong>{activity.user}</Text>
                      <Text className="text-sm text-start">{activity.description}</Text>
                    </div>
                    {activity.metadata?.note && (
                      <Text className="text-xs text-start text-gray-600 whitespace-pre-wrap">
                        {activity.metadata.note}
                      </Text>
                    )}
                  </div>
                }
                description={
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    {activity.metadata?.oldStatus && activity.metadata?.newStatus && (
                      <>
                        <Tag color="default" className="text-xs">
                          {activity.metadata.oldStatus}
                        </Tag>
                        <Text type="secondary">→</Text>
                        <Tag color="blue" className="text-xs">
                          {activity.metadata.newStatus}
                        </Tag>
                        <Text type="secondary">•</Text>
                      </>
                    )}
                    <Text type="secondary">
                      {formatDistanceToNow(new Date(activity.timestamp), {
                        addSuffix: true,
                      })}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <div className="py-8 text-center">
          <Text type="secondary">No activity yet</Text>
        </div>
      )}
    </div>
  );
};

export default QuoteRecentActivity;
